import { userFragment } from "MessagingV2/modules/MessagingFragments";
import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";

export const chatDetailsFragment = gql`
  fragment chatDetailsItem on Chat {
    id
    type
    label
    isMuted
    lastActiveAt
    unreadMessageCount
    viewOnlyRoles
    createdAt
    lastReadMessage {
      id
      cursor
    }
    personalChatReceiverUser {
      ...userDetailItem
    }
  }
  ${userFragment.userDetailItem}
`;

export const chatAndChannelDetailsFragment = gql`
  fragment chatAndChannelDetailsItem on Chat {
    id
    description
    profileImageData {
      url
      icon
    }
    isActive
    isArchived
    totalUsersCount
    type
    label
    isMuted
    lastActiveAt
    unreadMessageCount
    viewOnlyRoles
    createdAt
    lastReadMessage {
      id
      cursor
    }
    isUserMember
    personalChatReceiverUser {
      ...userDetailItem
      ... on Student {
        alertTypes {
          ...alertTypeFields
        }
      }
    }
  }

  ${userFragment.userDetailItem}
  ${studentsAlertFragment}
`;

export const channelBasicDetailsFragment = gql`
  fragment channelBasicDetailsItem on Chat {
    id
    label
    description
    profileImageData {
      url
      icon
    }
    isMuted
    isActive
    isArchived
    totalUsersCount
    type
    lastActiveAt
    unreadMessageCount
    lastReadMessage {
      id
      cursor
    }
  }
`;

export const channelDetailsFragment = gql`
  fragment chatDetailsItem on Chat {
    ...channelBasicDetailsItem
    users: usersV2 {
      edges {
        node {
          ...userDetailItem
        }
      }
    }
  }
  ${userFragment.userDetailItem}
  ${channelBasicDetailsFragment}
`;

export const getChatListQuery = gql`
  query getChatList($organizationId: ID!, $input: ChatFeedInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        name
        chats(input: $input) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...chatDetailsItem
            }
          }
        }
      }
    }
  }
  ${chatDetailsFragment}
`;

export const getAllChatListQuery = gql`
  query getAllChatList($organizationId: ID!, $input: AllChatFeedInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        name
        allChats(input: $input) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...chatDetailsItem
            }
          }
        }
      }
    }
  }
  ${chatDetailsFragment}
`;

export const getChannelListQuery = gql`
  query getChannelList($organizationId: ID!, $input: ChatFeedInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        name
        chats(input: $input) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...channelBasicDetailsItem
            }
          }
        }
      }
    }
  }
  ${channelBasicDetailsFragment}
`;

export const getChatAndChannelListQuery = gql`
  query getChatAndChannelList($organizationId: ID!, $input: ChatFeedInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        chats(input: $input) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...chatAndChannelDetailsItem
            }
          }
        }
      }
    }
  }
  ${chatAndChannelDetailsFragment}
`;

export const getChatAndChannelByIdQuery = gql`
  query getChatAndChannelById($chatId: ID!, $organizationId: ID!) {
    node(id: $chatId, type: CHAT) {
      ...chatAndChannelDetailsItem
    }
  }
  ${chatAndChannelDetailsFragment}
`;

export const getChatByIdQuery = gql`
  query getChatById($chatId: ID!, $organizationId: ID!) {
    node(id: $chatId, type: CHAT) {
      ...chatDetailsItem
    }
  }
  ${chatDetailsFragment}
`;

export const getChannelThreadIemByIdQuery = gql`
  query getChannelThreadIemById($chatId: ID!) {
    node(id: $chatId, type: CHAT) {
      ...channelBasicDetailsItem
    }
  }
  ${channelBasicDetailsFragment}
`;

export const getChatUnreadCountQuery = gql`
  query getChatUnreadCount(
    $organizationId: ID!
    $inputChat: ChatFeedInput
    $inputChannel: ChatFeedInput
    $inputAll: ChatFeedInput
    $skipChatCount: Boolean = false
    $skipChannelCount: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        chats {
          chatUnreadCount: totalCount(input: $inputChat)
            @skip(if: $skipChatCount)
          channelUnreadCount: totalCount(input: $inputChannel)
            @skip(if: $skipChannelCount)
          allUnreadCount: totalCount(input: $inputAll)
        }
      }
    }
  }
`;

export const getTotalChatAndChannelCountQuery = gql`
  query getTotalChatAndChannelCount(
    $organizationId: ID!
    $inputStaffAndStudentChat: ChatFeedInput
    $inputForTotalChannel: ChatFeedInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        chats {
          chatStaffAndStudentCount: totalCount(input: $inputStaffAndStudentChat)
          totalChannelCount: totalCount(input: $inputForTotalChannel)
        }
      }
    }
  }
`;

export const getChatSuggestionsQuery = gql`
  query getChatSuggestions($organizationId: ID!, $input: ChatSuggestionInput!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        chatSuggestions(input: $input) {
          chats {
            ...chatAndChannelDetailsItem
          }
          studentsToCreateFamilyChannel {
            id
            firstName
            lastName
            middleName
            familyMembers {
              id
            }
          }
          usersToCreatePersonalChat {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${chatAndChannelDetailsFragment}
  ${userFragment.userDetailItem}
`;
